import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, ShieldAlert, ShieldCheck, ShieldX, Loader2, RefreshCw, ArrowLeft, Copy, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const money = (n) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP', maximumFractionDigits: 2 }).format(Number(n || 0));
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const IdentityRow = ({ label, value, mono, testid }) => (
  <div className="flex items-start gap-3 text-xs">
    <span className="text-[10px] uppercase tracking-wider text-[#71717A] shrink-0 w-24">{label}</span>
    <span
      className={`flex-1 break-all ${mono ? 'font-mono' : ''} text-[#E4E4E7]`}
      data-testid={testid}
    >
      {value || '—'}
    </span>
    {value && (
      <button
        type="button"
        onClick={() => { navigator.clipboard.writeText(value); toast.success(`${label} copied`); }}
        className="text-[#71717A] hover:text-[#00F0FF] transition"
        title="Copy"
      >
        <Copy size={12} />
      </button>
    )}
  </div>
);

const OrderReviewPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [threshold, setThreshold] = useState(1000);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchList = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/orders/pending-review`);
      setOrders(data.orders || []);
      setThreshold(data.threshold_php || 1000);
    } catch (err) {
      toast.error('Failed to load pending orders.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []);

  const approve = async (id) => {
    if (!window.confirm('Approve this order and send the download link to the buyer?')) return;
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/orders/${id}/approve`, {});
      toast.success('Approved — download email sent.');
      fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Approve failed.');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id) => {
    const reason = window.prompt('Reason for rejection (optional):') || '';
    if (reason === null) return;
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/orders/${id}/reject`, { reason });
      toast.success('Rejected — buyer will not receive files.');
      fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reject failed.');
    } finally {
      setBusyId(null);
    }
  };

  const blockIdentity = async (type, value) => {
    if (!value) return;
    if (!window.confirm(`Block ${type}: ${value}? Future orders from them will be refused.`)) return;
    try {
      await axios.post(`${API}/admin/blocklist`, { type, value, reason: 'Blocked from review dashboard' });
      toast.success(`${type.toUpperCase()} blocked: ${value}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Block failed.');
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <div className="max-w-6xl mx-auto p-6 sm:p-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <button onClick={() => navigate('/admin/home')} className="text-xs text-[#71717A] hover:text-[#00F0FF] inline-flex items-center gap-1 mb-2">
              <ArrowLeft size={12} /> Admin home
            </button>
            <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
              <ShieldAlert className="text-[#FFB020]" size={28} /> Order review
            </h1>
            <p className="text-sm text-[#A1A1AA] mt-1">
              Orders of {money(threshold)} or more are held until you release them. Buyers get an email letting them know their order is being reviewed.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin/blocklist" className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full bg-[#18181B] border border-[#27272A] hover:border-[#00F0FF] hover:text-[#00F0FF] transition" data-testid="link-blocklist">
              <Shield size={12} /> Blocklist
            </Link>
            <button onClick={fetchList} className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full bg-[#18181B] border border-[#27272A] hover:border-[#00F0FF] hover:text-[#00F0FF] transition" data-testid="refresh-review">
              <RefreshCw size={12} /> Refresh
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-[#71717A]"><Loader2 className="mx-auto animate-spin mb-3" /> Loading pending orders…</div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16 bg-[#121214] border border-[#27272A] rounded-lg" data-testid="review-empty">
            <ShieldCheck className="mx-auto text-[#00F0FF] mb-3" size={40} />
            <p className="text-sm">No orders waiting for review. All caught up.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((o) => (
              <div key={o.id} className="bg-[#121214] border border-[#27272A] rounded-lg p-5" data-testid={`review-card-${o.id}`}>
                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                  <div className="lg:w-2/3 space-y-4">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-xs font-mono text-[#71717A]">#{o.id.slice(0, 8)}</span>
                      <span className="px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#FFB020]/15 text-[#FFB020] border border-[#FFB020]/30">Pending review</span>
                      <span className="text-lg font-semibold text-[#00F0FF]" data-testid={`review-total-${o.id}`}>{money(o.total_amount)}</span>
                      <span className="text-[10px] text-[#71717A]">{fmtDate(o.created_at)}</span>
                    </div>
                    <div className="space-y-2 bg-[#0F0F11] rounded-md p-4 border border-[#27272A]">
                      <IdentityRow label="Buyer" value={o.full_name} testid={`review-name-${o.id}`} />
                      <IdentityRow label="Email" value={o.email} testid={`review-email-${o.id}`} />
                      <IdentityRow label="IP" value={o.ip_address} mono testid={`review-ip-${o.id}`} />
                      <IdentityRow label="User agent" value={(o.user_agent || '').slice(0, 300)} testid={`review-ua-${o.id}`} />
                      <IdentityRow label="Session" value={(o.session_id || '').slice(0, 40)} mono />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-[#71717A] mb-1">Items</p>
                      <ul className="space-y-1 text-sm">
                        {(o.items || []).map((it, i) => (
                          <li key={i} className="flex justify-between border-b border-[#27272A] py-1">
                            <span>{it.album_title || it.album_id}</span>
                            <span className="font-mono text-[#A1A1AA]">{money(it.price)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="lg:w-1/3 space-y-2">
                    <Button
                      onClick={() => approve(o.id)}
                      disabled={busyId === o.id}
                      className="w-full bg-[#00F0FF] hover:bg-[#33F3FF] text-black rounded-full"
                      data-testid={`approve-btn-${o.id}`}
                    >
                      {busyId === o.id ? <Loader2 className="animate-spin mr-2" size={16} /> : <ShieldCheck className="mr-2" size={16} />}
                      Approve & send files
                    </Button>
                    <Button
                      onClick={() => reject(o.id)}
                      disabled={busyId === o.id}
                      variant="outline"
                      className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10 rounded-full"
                      data-testid={`reject-btn-${o.id}`}
                    >
                      <ShieldX className="mr-2" size={16} /> Reject
                    </Button>
                    <div className="pt-3 border-t border-[#27272A]">
                      <p className="text-[10px] uppercase tracking-wider text-[#71717A] mb-2">Anti-abuse</p>
                      <button
                        onClick={() => blockIdentity('ip', o.ip_address)}
                        disabled={!o.ip_address}
                        className="w-full text-xs px-3 py-2 rounded-full bg-[#18181B] border border-[#27272A] hover:border-red-500 hover:text-red-400 transition disabled:opacity-40 mb-2 inline-flex items-center justify-center gap-2"
                        data-testid={`block-ip-${o.id}`}
                      >
                        <Ban size={12} /> Block IP
                      </button>
                      <button
                        onClick={() => blockIdentity('email', o.email)}
                        disabled={!o.email}
                        className="w-full text-xs px-3 py-2 rounded-full bg-[#18181B] border border-[#27272A] hover:border-red-500 hover:text-red-400 transition disabled:opacity-40 inline-flex items-center justify-center gap-2"
                        data-testid={`block-email-${o.id}`}
                      >
                        <Ban size={12} /> Block email
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderReviewPage;
